import {
  CartFlagsService,
  Discount,
  ProductDiscount,
  RetrieveCartItem,
  RetrieveCartResponse,
  SessionStorageKeys
} from '@aglenergy/shared-library';
import { ComponentInterface, Component, JSX, State, h } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { BundleTileItems, DisplayItem } from 'app/shared/models/bundle/displayCartItem.model';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { ProductCategory } from 'app/shared/models/product-category.model';
import { SelectedProductSessionData } from 'app/shared/models/sessionData';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { bundleService } from 'app/shared/services/bundle/bundle.service';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { flowService } from 'app/shared/services/flow/flow-service';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';

@Component({
  tag: 'bundle-builder',
  styleUrl: 'bundle-builder.scss'
})
export class BundleBuilder implements ComponentInterface {
  @State() private cartDetails: RetrieveCartResponse;
  @State() private bundleTileItems: BundleTileItems;
  private cartFlagsService;
  private cartListener: () => void;
  private addProductToCartItemListener: () => void;
  private removeProductFromCartItemListener: () => void;

  public componentWillLoad(): void {
    bundleService.toggleStaticContent('none');
    const sessionStoreProducts = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
    this.setMobilePlansFromSessionStore(sessionStoreProducts);
    this.mapBundleTiles();
    this.initialiseBundleSelection();
    store.on('dispose', () => {
      this.cartListener();
      this.addProductToCartItemListener();
      this.removeProductFromCartItemListener();
    });
  }

  public componentDidRender(): void {
    this.handleCustomEvents();
  }

  public disconnectedCallback(): void {
    this.cartListener();
    this.addProductToCartItemListener();
    this.removeProductFromCartItemListener();
  }

  public setBundleSelection(bundleProductType: ProductCategory): void {
    store.set(StoreKeys.guidedFlowActiveCategory, bundleProductType);
    flowService.setActiveCategory(bundleProductType);
    this.scrollToProductCards(bundleProductType);
    switch (bundleProductType) {
      case 'energy':
      case 'nbn': {
        const nbnResponse = store.get(StoreKeys.bundleSearchStore)?.nbnResponse;
        store.set(StoreKeys.productDetails, nbnResponse);
        store.set(StoreKeys.productCategory, bundleProductType === 'energy' ? 'energy' : 'nbn');
        break;
      }
      case 'mobile plan': {
        const mobileResponse = store.get(StoreKeys.bundleSearchStore)?.mobileResponse;
        store.set(StoreKeys.productDetails, mobileResponse);
        store.set(StoreKeys.productCategory, 'mobile plan');
        break;
      }
      default:
        break;
    }
  }

  private initialiseBundleSelection() {
    let activeCategory = 'none' as ProductCategory | 'none';
    const lastActiveCategory = flowService.getActiveCategory();
    if (lastActiveCategory) {
      switch (lastActiveCategory.toLowerCase()) {
        case 'energy':
          activeCategory = 'energy';
          break;
        case 'nbn':
          activeCategory = 'nbn';
          break;
        case 'mobile plan':
        case 'mobile%20plan':
          activeCategory = 'mobile plan';
          break;
        default:
          break;
      }
    }
    store.set(StoreKeys.guidedFlowActiveCategory, activeCategory);
  }

  private mapBundleTiles(): void {
    this.cartListener = store.onChange(StoreKeys.cart, (retrieveCartResponse: RetrieveCartResponse) => {
      if (store.get('retrieveTriggeredFromRemove')) {
        if (retrieveCartResponse) {
          const cartWithSessionMobile: RetrieveCartResponse = {
            ...retrieveCartResponse,
            cartItems: [
              ...retrieveCartResponse.cartItems.filter((x) => x.additionalData.productCategory.toLowerCase() !== 'mobile plan'),
              ...this.cartDetails.cartItems.filter((x) => x.additionalData.productCategory.toLowerCase() === 'mobile plan')
            ]
          };

          this.cartDetails = {
            ...this.cartDetails,
            ...cartWithSessionMobile
          };
        }
      }

      if (!store.get('retrieveTriggeredFromRemove')) {
        this.cartDetails = {
          ...this.cartDetails,
          ...retrieveCartResponse
        };
      }
      this.storeSelectedMobileProducts();
      this.setBundleTileItems();
    });

    this.addProductToCartItemListener = store.onChange(StoreKeys.addProductToCartItem, (cartItems: RetrieveCartItem[]) => {
      if (cartItems?.length) {
        this.initialiseCartDetails();
        const cartDetails: RetrieveCartResponse = { ...this.cartDetails };
        const productDiscounts = this.getBundleDiscount();
        cartItems.forEach((cartItem) => {
          if (productDiscounts) {
            cartItem.productDiscounts = { ...productDiscounts } as unknown as ProductDiscount[];
          }
        });
        cartDetails.cartItems = [...cartDetails.cartItems, ...cartItems];
        this.cartDetails = { ...this.cartDetails, ...cartDetails };
        this.setBundleTileItems();
      }
    });

    this.removeProductFromCartItemListener = store.onChange(StoreKeys.removeProductFromCartItem, (cartItem: RetrieveCartItem) => {
      if (cartItem) {
        const cartDetails: RetrieveCartResponse = { ...this.cartDetails };
        cartDetails.cartItems = cartDetails.cartItems.filter((_cartItem) => _cartItem.cartItemId !== cartItem.cartItemId);
        this.cartDetails = { ...this.cartDetails, ...cartDetails };
        this.setBundleTileItems();
      }
    });
  }

  private setBundleTileItems(): void {
    this.bundleTileItems = bundleService.mapBundleTileItems(this.cartDetails);
  }

  private scrollToProductCards(productCategory: ProductCategory) {
    let elementToScrollTo;
    if (productCategory === 'energy') {
      elementToScrollTo = 'app-products-energy-container';
    } else {
      elementToScrollTo = 'app-products-container';
    }
    setTimeout(() => {
      if (document.getElementsByTagName(elementToScrollTo)?.[0]) {
        document.getElementsByTagName('html')[0].scroll({
          top: document.getElementsByTagName(elementToScrollTo)[0].offsetTop,
          behavior: 'smooth'
        });
      }
    }, 0);
  }
  private onTileClicked = (e: CustomEvent<string>) => {
    let action: string;
    let eventData: string[] = null;
    switch (e.detail) {
      case 'addEnergyTile':
        this.setBundleSelection('energy');
        if (this.bundleTileItems?.energy?.displayItems?.length) {
          action = 'Edit';
          eventData = this.bundleTileItems.energy.displayItems.map((item: DisplayItem) => item.productName);
        } else {
          action = 'Add';
        }
        tealiumAnalyticService.trackBundleSelectionData(action, 'energy', eventData);
        break;
      case 'addInternetTile':
        this.setBundleSelection('nbn');
        if (this.bundleTileItems?.nbn?.displayItems?.length) {
          action = 'Edit';
          eventData = this.bundleTileItems.nbn.displayItems.map((item: DisplayItem) => item.productName);
        } else {
          action = 'Add';
        }
        tealiumAnalyticService.trackBundleSelectionData(action, 'nbn', eventData);
        break;
      case 'addMobileTile':
        this.setBundleSelection('mobile plan');
        if (this.bundleTileItems?.mobile?.displayItems?.length) {
          action = 'Edit';
          eventData = this.bundleTileItems.mobile.displayItems.map((item: DisplayItem) => item.productName);
        } else {
          action = 'Add';
        }
        tealiumAnalyticService.trackBundleSelectionData(action, 'mobile plan', eventData);
        break;
      default:
        break;
    }
    e.preventDefault();
  };

  private getTileTheme(productCategory: ProductCategory, isProductSelected: boolean, theme: string, themeTint): string {
    if (!isProductSelected) {
      return null;
    }
    let tileTheme = '';
    // Default theme
    switch (productCategory) {
      case 'energy':
        tileTheme = 'secondary01-tint-80';
        break;
      case 'nbn':
      case 'mobile plan':
        tileTheme = 'secondary04';
        break;
    }
    // Theme from retrieveCart
    if (theme) {
      tileTheme = `${theme}${themeTint}`;
    }
    return tileTheme;
  }

  public storeSelectedMobileProducts(): void {
    let selectedMobileProductDetails = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
    this.cartFlagsService = new CartFlagsService(this.cartDetails);
    const mobileProducts = this.cartFlagsService.getMobileProducts();
    /* istanbul ignore else */
    if (mobileProducts?.length) {
      const bundleProducts = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.bundleProducts));
      if (bundleProducts?.mobileResponse?.products?.length) {
        const productDetails = mobileProducts.map((_mobileProduct: RetrieveCartItem) => {
          let product = bundleProducts.mobileResponse.products.find((_product) => _mobileProduct.sku === _product.sku);
          product = { ...product, cartItemId: _mobileProduct.cartItemId };
          return { product };
        });
        if (!selectedMobileProductDetails) {
          const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
          customerProfile.customerOriginURL = window.location.href;
          customerProfile.customerType = 'R';
          customerProfileService.setCustomerProfile(customerProfile);
          selectedMobileProductDetails = { customerProfile, productDetails };
        } else {
          productDetails.forEach((_productDetail) => {
            const productDetailExist = selectedMobileProductDetails.productDetails.find(
              (_selectedProductDetail) => _selectedProductDetail.product?.cartItemId === _productDetail?.product?.cartItemId
            );
            if (!productDetailExist) {
              selectedMobileProductDetails.productDetails.push(_productDetail);
            }
          });
        }
      }
    }

    // There are scenarios where mobile plans has been added in selectedMobileProducts store
    // but still not in the cart.
    if (selectedMobileProductDetails?.productDetails?.length !== mobileProducts?.length) {
      this.setMobilePlansFromSessionStore(selectedMobileProductDetails, mobileProducts);
    }
    if (selectedMobileProductDetails) {
      productsSessionStorageService.saveMobileProducts(selectedMobileProductDetails);
    }
  }

  private setMobilePlansFromSessionStore(sessionStoreProducts: SelectedProductSessionData, mobileProducts: RetrieveCartItem[] = []): void {
    if (sessionStoreProducts) {
      this.initialiseCartDetails();
      const productDiscounts: Discount[] = this.getBundleDiscount();
      const cartItems = [];
      sessionStoreProducts.productDetails.forEach((_productDetail) => {
        if (_productDetail) {
          const productDetailExist = [...mobileProducts]?.find(
            (_mobileProductInCart) => _mobileProductInCart?.cartItemId === _productDetail?.product?.cartItemId
          );
          if (!productDetailExist && _productDetail?.product) {
            const cartItem = bundleService.mapMobileProductCatalogueAsCartItem(_productDetail?.product, productDiscounts);
            cartItems.push(cartItem);
          }
        }
      });
      if (cartItems.length) {
        this.cartDetails.cartItems = [...this.cartDetails.cartItems, ...cartItems];
        this.setBundleTileItems();
      }
    }
  }

  private getBundleDiscount(): Discount[] {
    if (this.cartFlagsService) {
      const cartFlags = this.cartFlagsService.getCartFlags();
      return cartFlags?.hasEnergy ? bundleService.getBundleDiscountByCategory() : null;
    }
    return null;
  }

  private handleCustomEvents(): void {
    const appComparisonElement = document.querySelector('#app-comparison');
    if (appComparisonElement) {
      appComparisonElement.addEventListener('cart', (e: CustomEvent) => store.set(StoreKeys.cart, JSON.parse(e.detail)));
    }
  }

  private initialiseCartDetails(): void {
    if (!this.cartDetails) {
      this.cartDetails = { cartItems: [] };
    }
  }

  private getErrorState(product: ProductCategory): string {
    const bundleStore = store.get(StoreKeys.bundleSearchStore);
    let error = null;
    if (bundleStore) {
      switch (product) {
        case 'energy':
          error = bundleStore.energyResponse?.siteError ? 'SERVICE ERROR' : null;
          if (!error) {
            error = bundleStore.energyResponse.electricity.siteError && bundleStore.energyResponse.gas.siteError ? 'SITE ERROR' : null;
          }
          break;
        case 'nbn':
          error = bundleStore?.nbnResponse?.siteError || !bundleStore.nbnResponse?.serviceQualification?.nbn ? 'SERVICE ERROR' : null;
          if (!error) {
            error = bundleStore.nbnResponse.serviceQualification.nbn.result === 'FAIL' ? 'SITE ERROR' : null;
          }
          break;
        default:
          break;
      }
    }
    return error;
  }

  private getErrorTile(product: ProductCategory, errorDescription: string): JSX.Element {
    let tileId: string;
    let icon: string;
    let heading: string;
    let body: string;

    switch (product) {
      case 'energy':
        tileId = 'energyTileDisabled';
        icon = '/apps/comparison/assets/icon/agl_tile_electricity--muted.svg';
        heading = 'Energy';
        if (errorDescription === 'SERVICE ERROR') {
          body = 'We are unable to provide an energy service at your address.';
        }
        if (errorDescription === 'SITE ERROR') {
          body = `We can't provide energy to your address at this time.`;
        }
        break;
      case 'nbn':
        tileId = 'internetTileDisabled';
        icon = '/apps/comparison/assets/icon/agl_tile_internet--muted.svg';
        heading = 'Internet';
        if (errorDescription === 'SERVICE ERROR') {
          body = 'We are unable to provide an internet service at your address.';
        }
        if (errorDescription === 'SITE ERROR') {
          body = `We can't provide internet to your address at this time.`;
        }
        break;
      default:
        tileId = 'tileDisabled';
        break;
    }
    return (
      <agl-ds-tile tileId={tileId} hoverable={false}>
        <div slot="tile-heading" class="bundle__tile-heading">
          <div class="bundle__tile-heading__left">
            <agl-ds-icon icon-path={icon} size="xs" />
          </div>
          <div class="bundle__tile-heading__middle">
            <agl-ds-p styled-as="md" bottom-margin="space02" font="fontfamily02" appearance="muted">
              {heading}
            </agl-ds-p>
          </div>
        </div>
        <div slot="tile-content" class="bundle__tile-content">
          <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02" appearance="muted">
            {body}
          </agl-ds-p>
        </div>
      </agl-ds-tile>
    );
  }

  private getAuthenticatedEnergyTile() {
    const icon = '/apps/comparison/assets/icon/agl_tile_electricity--muted.svg';
    return (
      <agl-ds-tile tileId="energyTileDisabled" theme="secondary01-tint-80" active={false} hoverable={false}>
        <div slot="tile-heading" class="bundle__tile-heading">
          <div class="bundle__tile-heading__left">
            <agl-ds-icon icon-path={icon} size="xs" />
          </div>
          <div class="bundle__tile-heading__middle">
            <agl-ds-p styled-as="md" bottom-margin="space02" font="fontfamily02" appearance="muted">
              Energy
            </agl-ds-p>
          </div>
          <div class="bundle__tile-heading__right">
            <agl-ds-icon icon-path="/apps/comparison/assets/icon/agl_tile_tick.svg" size="xs" />
          </div>
        </div>
        <div slot="tile-content" class="bundle__tile-content">
          <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
            <agl-ds-text fontWeight="semibold">You already have energy with us.</agl-ds-text>
          </agl-ds-p>
          <agl-ds-p styled-as="xs" bottom-margin="none" font="fontfamily02">
            <agl-ds-text>
              If you would like to change your energy plan please visit&nbsp;
              <agl-ds-link href="https://myaccount.agl.com.au " sr-context="My Account">
                My Account
              </agl-ds-link>{' '}
              or call <b>131 245</b>
            </agl-ds-text>
          </agl-ds-p>
        </div>
      </agl-ds-tile>
    );
  }

  private getTile(product: ProductCategory, isAuthenticated: boolean): JSX.Element {
    const error = this.getErrorState(product);
    if (error || store.get(StoreKeys.responseError) || (error && isAuthenticated && product === 'nbn')) {
      return this.getErrorTile(product, error);
    }
    if (isAuthenticated && product === 'energy') {
      return this.getAuthenticatedEnergyTile();
    }
    let tileId: string;
    let productFound: boolean;
    let productTheme: string;
    let productTint: string;
    let displayItems: DisplayItem[];
    let subtotal: string;
    let heading: string;
    let icon: string;
    switch (product) {
      case 'energy':
        tileId = 'addEnergyTile';
        icon = '/apps/comparison/assets/icon/agl_tile_electricity.svg';
        productFound = !!this.bundleTileItems?.energy?.displayItems?.length;
        productTheme = this.bundleTileItems?.energy?.theme;
        productTint = this.bundleTileItems?.energy?.themeTint;
        displayItems = this.bundleTileItems?.energy?.displayItems;
        heading = productFound ? 'Energy' : 'Add Energy';
        break;
      case 'nbn':
        tileId = 'addInternetTile';
        icon = '/apps/comparison/assets/icon/agl_tile_internet.svg';
        productFound = !!this.bundleTileItems?.nbn?.displayItems?.length;
        productTheme = this.bundleTileItems?.nbn?.theme;
        productTint = this.bundleTileItems?.nbn?.themeTint;
        displayItems = this.bundleTileItems?.nbn?.displayItems;
        subtotal = this.bundleTileItems?.nbn?.subtotal;
        heading = productFound ? 'Internet' : 'Add Internet';
        break;
      case 'mobile plan':
        tileId = 'addMobileTile';
        icon = '/apps/comparison/assets/icon/agl_tile_mobile.svg';
        productFound = !!this.bundleTileItems?.mobile?.displayItems?.length;
        productTheme = this.bundleTileItems?.mobile?.theme;
        productTint = this.bundleTileItems?.mobile?.themeTint;
        displayItems = this.bundleTileItems?.mobile?.displayItems;
        subtotal = this.bundleTileItems?.mobile?.subtotal;
        heading = productFound ? 'Mobile' : 'Add Mobile';
        break;
      default:
        break;
    }

    const tileTheme = this.getTileTheme(product, productFound, productTheme, productTint);
    return (
      <agl-ds-tile
        tileId={tileId}
        theme={tileTheme}
        active={store.get(StoreKeys.guidedFlowActiveCategory) === product}
        hoverable={true}
        onTileClicked={this.onTileClicked}
      >
        <div slot="tile-heading" class="bundle__tile-heading">
          <div class="bundle__tile-heading__left">
            <agl-ds-icon icon-path={icon} size="xs" />
          </div>
          <div class="bundle__tile-heading__middle">
            <agl-ds-p styled-as="md" bottom-margin="space02" font="fontfamily02">
              {heading}
            </agl-ds-p>
          </div>
          <div class="bundle__tile-heading__right">
            {!productFound && <agl-ds-icon icon-path="/apps/comparison/assets/icon/agl_tile_circle_plus.svg" size="xs" />}
            {productFound && (
              <agl-ds-text fontWeight="semibold">
                <agl-ds-link styled-as="md" appearance="default" open-new-window="true" href="#" sr-context="Edit">
                  Edit
                </agl-ds-link>
              </agl-ds-text>
            )}
          </div>
        </div>
        <div slot="tile-content" class="bundle__tile-content">
          {!productFound && this.tileBodyNoProduct(product)}
          {productFound && this.tileBodyProduct(product, displayItems, subtotal)}
        </div>
      </agl-ds-tile>
    );
  }

  private tileBodyNoProduct(noProduct: ProductCategory): JSX.Element {
    if (noProduct === 'energy') {
      return (
        <div>
          <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
            <agl-ds-text fontWeight="semibold">Choose your energy plan and save on internet and mobile.</agl-ds-text>
          </agl-ds-p>
        </div>
      );
    }
    const energyError = this.getErrorState('energy');
    return (
      <div>
        {energyError && (
          <div>
            <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02" font-weight="bold">
              <agl-ds-text font-weight="semibold">
                {noProduct === 'nbn'
                  ? 'All plans come with unlimited data and no lock-in contract.'
                  : 'All plans come with unlimited standard national talk and texts.'}
              </agl-ds-text>
            </agl-ds-p>
          </div>
        )}
        {!energyError && (
          <div>
            <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02" font-weight="bold">
              Get monthly discounts on selected AGL{' '}
              <agl-ds-text font-weight={noProduct === 'nbn' ? 'semibold' : ''}>{noProduct === 'nbn' ? 'nbn' : 'mobile'}</agl-ds-text> plans
              when you have AGL energy.
            </agl-ds-p>
          </div>
        )}
      </div>
    );
  }

  private tileBodyProduct(product: ProductCategory, displayItems: DisplayItem[], subtotal: string) {
    if (product === 'energy') {
      return displayItems.map((item) => {
        return (
          <div>
            <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
              {item.productName}
            </agl-ds-p>
            <agl-ds-spacer orientation="vertical" size="space02" />
          </div>
        );
      });
    } else if (product === 'nbn') {
      return (
        <div>
          <div>
            <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
              {displayItems[0].productName}
            </agl-ds-p>
            <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
              {displayItems[0].productInfo}
            </agl-ds-p>
            <agl-ds-spacer orientation="vertical" size="space02" />
          </div>
          <div>
            <agl-ds-p bottomMargin="none" font="fontfamily02">
              <agl-ds-text styledAs="md" fontWeight="semibold">
                {subtotal}
              </agl-ds-text>
            </agl-ds-p>
            {displayItems[0].paymentFrequency && displayItems[0].paymentPlanPrice && (
              <agl-ds-text styledAs="sm">
                {this.getPaymentPlanText(displayItems[0].paymentFrequency, displayItems[0].paymentPlanPrice)}
              </agl-ds-text>
            )}
          </div>
        </div>
      );
    } else if (product === 'mobile plan') {
      return (
        <div>
          {displayItems.map((item) => {
            return (
              <div>
                <div>
                  <agl-ds-p styled-as="md" bottom-margin="none" font="fontfamily02">
                    {`${item.productName} ${item.itemCount > 1 ? ` x${item.itemCount}` : ''}`}
                  </agl-ds-p>
                  <agl-ds-spacer orientation="vertical" size="space02" />
                </div>
              </div>
            );
          })}
          <div>
            <agl-ds-p bottom-margin="none" styled-as="md" font="fontfamily02">
              <agl-ds-text styled-as="md" font-weight="semibold">
                {subtotal}
              </agl-ds-text>
            </agl-ds-p>
            <agl-ds-spacer orientation="vertical" size="space01" />
          </div>
        </div>
      );
    }
  }

  private getPaymentPlanText(paymentFrequency: string, paymentPlanPrice: string): string {
    switch (paymentFrequency) {
      case 'outright':
        return 'not incl upfront cost of modem';
      case 'twelveMonths':
        return `incl ${paymentPlanPrice} each month for 12 mths`;
      case 'twentyFourMonths':
        return `incl ${paymentPlanPrice} each month for 24 mths`;
      default:
        return '';
    }
  }

  private toggleEnergyContainer(showContent: boolean): void {
    if (showContent === null) {
      return;
    }
    const energyContainerSectionElem = document
      .querySelector('[spa-id="app-products-energy-container"]')
      ?.closest('section.container') as HTMLElement;
    if (energyContainerSectionElem) {
      energyContainerSectionElem.style.display = showContent ? 'block' : 'none';
    }
  }

  private shouldShowBundleBuilder(): boolean {
    const error = store.get('responseError');
    const address = store.get('address');
    const sessionAddress = sessionStorage.getItem(SessionStorageKeys.address);
    const showBundleBuilder = (address || sessionAddress) && ((!error || error.errorType !== 'systemError') as boolean);
    return showBundleBuilder;
  }

  private showErrorBoundary(): boolean {
    const error = store.get(StoreKeys.responseError);
    const flowTypeError = JSON.parse(sessionStorage.getItem(SessionStorageKeys.FlowDetails))?.error;
    return error?.errorType || flowTypeError;
  }

  public render(): JSX.Element {
    const showBundleBuilder = this.shouldShowBundleBuilder();
    this.toggleEnergyContainer(showBundleBuilder);
    const selectedCategory = store.get(StoreKeys.guidedFlowActiveCategory);
    bundleService.toggleStaticContent(selectedCategory);
    return (
      <div>
        {showBundleBuilder && (
          <div class="bundle-builder">
            <div class="bundle__intro">
              <agl-ds-text styled-as="lg" bottom-margin="space01" font-weight="semibold">
                Here's what's available at your address:
              </agl-ds-text>
            </div>
            <div class="bundle__container">
              <div class="bundle__product">
                {this.getTile(
                  'energy',
                  store.get('isAuthenticated') && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true')
                )}
              </div>
              <div class="bundle__product">
                {this.getTile(
                  'nbn',
                  store.get('isAuthenticated') && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true')
                )}
              </div>
              <div class="bundle__product">
                {this.getTile(
                  'mobile plan',
                  store.get('isAuthenticated') && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true')
                )}
              </div>
              <div class="bundle__product">
                <bundle-summary cartDetails={this.cartDetails} />
              </div>
            </div>
          </div>
        )}
        {!showBundleBuilder && this.showErrorBoundary() && <error-boundary />}
      </div>
    );
  }
}
